/**
 * Tools Hub Screen
 * Marketing page listing all financial tools available on Wealfi
 * URL: https://wealfi.co/fr/tools
 */

import React, { useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Pressable,
  useWindowDimensions,
  Platform,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import useDomain from '../../hooks/useDomain';
import useWebSEO from '../../hooks/useWebSEO';
import { analytics, AnalyticsEvent } from '../../utils/analytics';

// Wealfi Brand Colors
const brandColors = {
  green: '#71B352',
  greenDark: '#5B8A3C',
  greenLight: '#E8F5E0',
  dark: '#3D4543',
  orange: '#D97234',
  orangeLight: '#FFF3EB',
  gray: '#6B7280',
  background: '#FAFBFC',
  white: '#FFFFFF',
};

// Tool card definitions
interface ToolCard {
  id: string;
  icon: string;
  title: string;
  description: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
  status: 'active' | 'coming_soon';
  route?: string;
}

const tools: ToolCard[] = [
  {
    id: 'compound-interest',
    icon: 'chart-line',
    title: 'Calculateur Intérêts Composés',
    description:
      'Simule combien tes économies peuvent devenir grâce aux intérêts composés. 50€/mois × 20 ans = ?',
    badge: 'Gratuit',
    badgeColor: brandColors.greenDark,
    badgeBg: brandColors.greenLight,
    status: 'active',
    route: '/fr/tools/compound-interest-calculator',
  },
  {
    id: 'budget-50-30-20',
    icon: 'wallet-outline',
    title: 'Calculateur Budget 50/30/20',
    description:
      'Répartis ton budget selon la règle 50/30/20 (besoins/envies/épargne).',
    badge: 'Bientôt',
    badgeColor: brandColors.orange,
    badgeBg: brandColors.orangeLight,
    status: 'coming_soon',
  },
  {
    id: 'borrowing-capacity',
    icon: 'home-outline',
    title: "Simulateur Capacité d'Emprunt",
    description:
      'Combien peux-tu emprunter selon ton salaire et tes charges ?',
    badge: 'Bientôt',
    badgeColor: brandColors.orange,
    badgeBg: brandColors.orangeLight,
    status: 'coming_soon',
  },
  {
    id: 'debt-repayment',
    icon: 'credit-card-outline',
    title: 'Calculateur Remboursement Dettes',
    description:
      'Plan optimal pour rembourser tes dettes (avalanche vs boule de neige).',
    badge: 'Bientôt',
    badgeColor: brandColors.orange,
    badgeBg: brandColors.orangeLight,
    status: 'coming_soon',
  },
];

export default function ToolsListScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { isLandingDomain, appUrl } = useDomain();
  const scrollRef = useRef<ScrollView>(null);

  // SEO metadata
  useWebSEO({
    title: 'Outils Financiers Gratuits – Wealfi',
    description:
      "Accède à nos outils financiers gratuits : calculateur d'intérêts composés, simulateur de budget, analyse patrimoine. Pilote ton argent intelligemment.",
    ogUrl: 'https://wealfi.co/fr/tools',
    ogImage: 'https://wealfi.co/og-images/tools.png',
    canonical: 'https://wealfi.co/fr/tools',
  });

  // Track page view on mount
  useEffect(() => {
    analytics.initialize();
    analytics.trackEvent(AnalyticsEvent.TOOLS_HUB_VIEWED);
  }, []);

  // Navigation handlers
  const handleGetStarted = useCallback(() => {
    analytics.trackEvent(AnalyticsEvent.TOOLS_HUB_TOOL_CLICKED, { section: 'cta' });
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/signup`;
      return;
    }
    navigation.navigate('Auth', { screen: 'SignUp' });
  }, [isLandingDomain, appUrl, navigation]);

  const handleLogin = useCallback(() => {
    if (Platform.OS === 'web' && isLandingDomain) {
      window.location.href = `${appUrl}/login`;
      return;
    }
    navigation.navigate('Auth', { screen: 'Login' });
  }, [isLandingDomain, appUrl, navigation]);

  const handleLogoPress = useCallback(() => {
    if (Platform.OS === 'web') {
      window.location.href = '/fr';
      return;
    }
    navigation.navigate('Landing');
  }, [navigation]);

  const handleToolPress = useCallback(
    (tool: ToolCard) => {
      analytics.trackEvent(AnalyticsEvent.TOOLS_HUB_TOOL_CLICKED, {
        toolName: tool.id,
        toolStatus: tool.status,
      });

      if (tool.status === 'active' && tool.route) {
        if (Platform.OS === 'web') {
          window.location.href = tool.route;
        } else {
          navigation.navigate('CompoundInterestCalculator');
        }
      }
      // Coming soon tools: tracking only, no navigation
    },
    [navigation],
  );

  // Render a single tool card
  const renderToolCard = (tool: ToolCard) => {
    const isComingSoon = tool.status === 'coming_soon';

    return (
      <Pressable
        key={tool.id}
        style={[
          styles.toolCard,
          isDesktop && styles.toolCardDesktop,
          isComingSoon && styles.toolCardComingSoon,
        ]}
        onPress={() => handleToolPress(tool)}
        accessibilityRole="button"
        accessibilityLabel={`${tool.title}${isComingSoon ? ' — bientôt disponible' : ''}`}
      >
        {/* Badge */}
        <View style={[styles.toolBadge, { backgroundColor: tool.badgeBg }]}>
          <Text style={[styles.toolBadgeText, { color: tool.badgeColor }]}>
            {tool.badge}
          </Text>
        </View>

        {/* Icon */}
        <View
          style={[
            styles.toolIconContainer,
            {
              backgroundColor: isComingSoon
                ? '#F3F4F6'
                : brandColors.greenLight,
            },
          ]}
        >
          <Icon
            name={tool.icon}
            size={32}
            color={isComingSoon ? brandColors.gray : brandColors.green}
          />
        </View>

        {/* Title */}
        <Text
          style={[styles.toolTitle, isComingSoon && styles.toolTitleComingSoon]}
        >
          {tool.title}
        </Text>

        {/* Description */}
        <Text
          style={[
            styles.toolDescription,
            isComingSoon && styles.toolDescriptionComingSoon,
          ]}
        >
          {tool.description}
        </Text>

        {/* CTA button for active tools */}
        {tool.status === 'active' && (
          <View style={styles.toolCta}>
            <Text style={styles.toolCtaText}>Calculer →</Text>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <View style={styles.container}>
      {/* Fixed Navigation */}
      <View style={[styles.navbar, isDesktop && styles.navbarDesktop]}>
        <Pressable onPress={handleLogoPress} style={styles.logoContainer}>
          <View style={styles.logoIcon}>
            <Text style={styles.logoEmoji}>🤖</Text>
          </View>
          <Text style={styles.logoText}>
            Wealfi
          </Text>
        </Pressable>

        {/* Desktop Navigation Links */}
        {isDesktop && (
          <View style={styles.navLinks}>
            <Pressable
              onPress={() => {
                if (Platform.OS === 'web') {
                  window.location.href = '/fr';
                }
              }}
              style={styles.navLink}
            >
              <Text style={styles.navLinkText}>Accueil</Text>
            </Pressable>
            <Pressable style={styles.navLink}>
              <Text style={[styles.navLinkText, styles.navLinkTextActive]}>
                Outils
              </Text>
            </Pressable>
          </View>
        )}

        <View style={styles.navActions}>
          {isDesktop && (
            <Pressable onPress={handleLogin} style={styles.navLinkButton}>
              <Text style={styles.navLinkText}>Connexion</Text>
            </Pressable>
          )}
          <Pressable
            onPress={handleGetStarted}
            style={[styles.ctaButton, !isDesktop && styles.ctaButtonMobile]}
          >
            <Text style={styles.ctaButtonText}>
              {isDesktop ? 'Essayer gratuitement' : 'Essayer'}
            </Text>
          </Pressable>
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Hero Section */}
        <View style={[styles.heroSection, isDesktop && styles.heroSectionDesktop]}>
          <View style={styles.heroInner}>
            <Text
              style={[styles.heroTitle, isDesktop && styles.heroTitleDesktop]}
              accessibilityRole="header"
            >
              Outils Financiers{' '}
              <Text style={styles.heroTitleAccent}>Gratuits</Text>
            </Text>

            <Text
              style={[
                styles.heroSubtitle,
                isDesktop && styles.heroSubtitleDesktop,
              ]}
            >
              Découvre nos outils pour mieux gérer ton budget et planifier ton
              avenir financier. Gratuits, sans inscription, disponibles 24/7.
            </Text>
          </View>
        </View>

        {/* Tools Grid */}
        <View
          style={[
            styles.toolsSection,
            isDesktop && styles.toolsSectionDesktop,
          ]}
        >
          <View style={styles.toolsInner}>
            <View
              style={[styles.toolsGrid, isDesktop && styles.toolsGridDesktop]}
            >
              {tools.map(renderToolCard)}
            </View>
          </View>
        </View>

        {/* CTA Section */}
        <View style={[styles.ctaSection, isDesktop && styles.ctaSectionDesktop]}>
          <View style={styles.ctaInner}>
            <View style={styles.ctaCard}>
              <Text
                style={[
                  styles.ctaSectionTitle,
                  isDesktop && styles.ctaSectionTitleDesktop,
                ]}
                accessibilityRole="header"
              >
                Va plus loin avec Wealfi
              </Text>

              <Text style={styles.ctaSectionText}>
                Ces outils te donnent un aperçu. Wealfi analyse ton budget
                réel, détecte des économies cachées, et t'aide à atteindre tes
                objectifs.
              </Text>

              <Pressable
                onPress={handleGetStarted}
                style={styles.ctaSectionButton}
              >
                <Text style={styles.ctaSectionButtonText}>
                  Essayer gratuitement →
                </Text>
              </Pressable>

              <Text style={styles.ctaSectionSubtext}>
                Gratuit. Sans CB. 2 minutes.
              </Text>
            </View>
          </View>
        </View>

        {/* SEO Content Section */}
        <View
          style={[
            styles.seoSection,
            isDesktop && styles.seoSectionDesktop,
          ]}
        >
          <View style={styles.seoInner}>
            <Text
              style={[
                styles.seoTitle,
                isDesktop && styles.seoTitleDesktop,
              ]}
              accessibilityRole="header"
            >
              Pourquoi utiliser nos outils financiers ?
            </Text>

            <Text style={styles.seoParagraph}>
              Gérer ses finances personnelles peut sembler complexe, mais les
              bons outils rendent tout plus simple. Que tu débutes dans la vie
              active ou que tu cherches à optimiser ton patrimoine, nos
              calculateurs financiers gratuits t'aident à prendre des décisions
              éclairées, en quelques clics.
            </Text>

            <Text style={styles.seoParagraph}>
              Notre calculateur d'intérêts composés te montre la puissance du
              temps sur tes économies. Albert Einstein appelait les intérêts
              composés « la huitième merveille du monde ». Avec seulement
              50 € par mois investis à 7 % pendant 20 ans, tu accumules plus
              de 26 000 € — dont plus de 14 000 € d'intérêts seuls. Visualise
              l'impact de chaque euro épargné et comprends pourquoi commencer
              tôt fait toute la différence.
            </Text>

            <Text style={styles.seoParagraph}>
              Le calculateur budget 50/30/20, bientôt disponible, t'aidera à
              répartir ton revenu selon une méthode éprouvée : 50 % pour tes
              besoins essentiels (loyer, courses, transport), 30 % pour tes
              envies (loisirs, restaurants, shopping), et 20 % pour ton épargne
              et tes investissements. C'est la base d'une gestion financière
              saine et durable.
            </Text>

            <Text style={styles.seoParagraph}>
              Tu te demandes combien tu peux emprunter pour acheter un
              appartement ? Notre simulateur de capacité d'emprunt (bientôt
              disponible) prendra en compte ton salaire, tes charges, et les
              taux actuels pour te donner une estimation réaliste. Plus besoin
              d'attendre un rendez-vous bancaire pour avoir une première idée
              de ton budget immobilier.
            </Text>

            <Text style={styles.seoParagraph}>
              Pour ceux qui jonglent avec plusieurs crédits, notre calculateur
              de remboursement de dettes comparera les stratégies avalanche
              (rembourser d'abord le taux le plus élevé) et boule de neige
              (rembourser d'abord le plus petit montant). Chaque stratégie a
              ses avantages — l'une optimise les intérêts payés, l'autre la
              motivation psychologique.
            </Text>

            <Text style={styles.seoParagraph}>
              Tous nos outils sont gratuits, sans inscription requise, et
              disponibles 24h/24. Tes données restent sur ton appareil et ne
              sont jamais transmises à des tiers. Pour aller plus loin, Wealfi
              AI analyse ton budget réel grâce à l'intelligence artificielle,
              détecte tes abonnements oubliés, tes frais bancaires évitables,
              et te propose un plan d'action personnalisé pour atteindre tes
              objectifs financiers.
            </Text>

            <Text style={styles.seoParagraph}>
              Que tu veuilles constituer une épargne de précaution, préparer
              l'achat de ton premier bien immobilier, ou simplement mieux
              comprendre où part ton argent chaque mois, nos outils et notre
              assistant IA sont là pour t'accompagner. Commence par un
              calculateur, continue avec Wealfi — ton argent mérite une
              stratégie.
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={[styles.footer, isDesktop && styles.footerDesktop]}>
          <View
            style={[
              styles.footerContent,
              isDesktop && styles.footerContentDesktop,
            ]}
          >
            <View
              style={[
                styles.footerBrand,
                isDesktop && styles.footerBrandDesktop,
              ]}
            >
              <Text style={styles.footerLogo}>
                Wealfi
              </Text>
              <Text style={styles.footerTagline}>
                Ton assistant financier IA qui optimise ton argent pendant que
                tu vis ta vie.
              </Text>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Produit</Text>
              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web') window.location.href = '/fr';
                }}
              >
                <Text style={styles.footerLink}>Accueil</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web')
                    window.location.href = '/fr/tools';
                }}
              >
                <Text style={styles.footerLink}>Outils gratuits</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  if (Platform.OS === 'web')
                    window.location.href =
                      '/fr/tools/compound-interest-calculator';
                }}
              >
                <Text style={styles.footerLink}>Intérêts composés</Text>
              </Pressable>
            </View>

            <View style={styles.footerLinks}>
              <Text style={styles.footerLinksTitle}>Légal</Text>
              <Pressable onPress={() => Linking.openURL('/fr/terms')}>
                <Text style={styles.footerLink}>CGU</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/privacy')}>
                <Text style={styles.footerLink}>Confidentialité</Text>
              </Pressable>
              <Pressable onPress={() => Linking.openURL('/fr/legal')}>
                <Text style={styles.footerLink}>Mentions légales</Text>
              </Pressable>
            </View>
          </View>

          <View style={styles.footerBottom}>
            <Text style={styles.footerCopyright}>
              © 2026 Wealfi. Tous droits réservés.
            </Text>
            <Text style={styles.footerDisclaimer}>
              Wealfi ne fournit pas de conseil en investissement au sens de
              l'AMF
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

// ============================================================
// STYLES
// ============================================================

const styles = StyleSheet.create({
  // Layout
  container: {
    flex: 1,
    backgroundColor: brandColors.white,
    overflow: 'hidden',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 70, // Space for fixed navbar
    overflow: 'hidden',
  },

  // Fixed Navbar
  navbar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  navbarDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 16,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: brandColors.greenLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoEmoji: {
    fontSize: 20,
  },
  logoText: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.dark,
  },
  logoTextAccent: {
    color: brandColors.green,
  },
  navLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 32,
  },
  navLink: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  navLinkButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  navLinkText: {
    fontSize: 14,
    fontWeight: '500',
    color: brandColors.gray,
  },
  navLinkTextActive: {
    color: brandColors.green,
    fontWeight: '600',
  },
  navActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  ctaButton: {
    backgroundColor: brandColors.green,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
  },
  ctaButtonMobile: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  ctaButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },

  // Hero Section
  heroSection: {
    paddingHorizontal: 16,
    paddingTop: 48,
    paddingBottom: 32,
    backgroundColor: brandColors.background,
  },
  heroSectionDesktop: {
    paddingHorizontal: 48,
    paddingTop: 80,
    paddingBottom: 48,
  },
  heroInner: {
    maxWidth: 800,
    marginHorizontal: 'auto',
    width: '100%',
    alignItems: 'center',
  },
  heroTitle: {
    fontSize: 32,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    lineHeight: 40,
    marginBottom: 16,
  },
  heroTitleDesktop: {
    fontSize: 48,
    lineHeight: 56,
  },
  heroTitleAccent: {
    color: brandColors.green,
  },
  heroSubtitle: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 26,
    maxWidth: 600,
  },
  heroSubtitleDesktop: {
    fontSize: 18,
    lineHeight: 28,
  },

  // Tools Section
  toolsSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.white,
  },
  toolsSectionDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 64,
  },
  toolsInner: {
    maxWidth: 1000,
    marginHorizontal: 'auto',
    width: '100%',
  },
  toolsGrid: {
    gap: 20,
  },
  toolsGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },

  // Tool Card
  toolCard: {
    backgroundColor: brandColors.white,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  toolCardDesktop: {
    flex: 1,
    minWidth: '45%',
    maxWidth: '48.5%',
  },
  toolCardComingSoon: {
    opacity: 0.6,
  },
  toolBadge: {
    alignSelf: 'flex-start',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 4,
    marginBottom: 16,
  },
  toolBadgeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  toolIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  toolTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 8,
  },
  toolTitleComingSoon: {
    color: brandColors.gray,
  },
  toolDescription: {
    fontSize: 14,
    color: brandColors.gray,
    lineHeight: 22,
    marginBottom: 16,
  },
  toolDescriptionComingSoon: {
    color: '#9CA3AF',
  },
  toolCta: {
    backgroundColor: brandColors.green,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignSelf: 'flex-start',
  },
  toolCtaText: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
  },

  // CTA Section
  ctaSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.background,
  },
  ctaSectionDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 64,
  },
  ctaInner: {
    maxWidth: 700,
    marginHorizontal: 'auto',
    width: '100%',
  },
  ctaCard: {
    backgroundColor: brandColors.white,
    borderRadius: 20,
    padding: 32,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  ctaSectionTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: brandColors.dark,
    textAlign: 'center',
    marginBottom: 12,
  },
  ctaSectionTitleDesktop: {
    fontSize: 32,
  },
  ctaSectionText: {
    fontSize: 16,
    color: brandColors.gray,
    textAlign: 'center',
    lineHeight: 26,
    marginBottom: 24,
    maxWidth: 500,
  },
  ctaSectionButton: {
    backgroundColor: brandColors.green,
    borderRadius: 24,
    paddingVertical: 14,
    paddingHorizontal: 32,
    marginBottom: 12,
  },
  ctaSectionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: brandColors.white,
  },
  ctaSectionSubtext: {
    fontSize: 13,
    color: brandColors.gray,
  },

  // SEO Content Section
  seoSection: {
    paddingHorizontal: 16,
    paddingVertical: 48,
    backgroundColor: brandColors.white,
  },
  seoSectionDesktop: {
    paddingHorizontal: 48,
    paddingVertical: 64,
  },
  seoInner: {
    maxWidth: 800,
    marginHorizontal: 'auto',
    width: '100%',
  },
  seoTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: brandColors.dark,
    marginBottom: 24,
  },
  seoTitleDesktop: {
    fontSize: 28,
  },
  seoParagraph: {
    fontSize: 15,
    color: brandColors.gray,
    lineHeight: 26,
    marginBottom: 16,
  },

  // Footer
  footer: {
    backgroundColor: brandColors.dark,
    paddingHorizontal: 16,
    paddingVertical: 48,
  },
  footerDesktop: {
    paddingHorizontal: 48,
  },
  footerContent: {
    flexDirection: 'column',
    gap: 32,
    marginBottom: 32,
  },
  footerContentDesktop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 48,
  },
  footerBrand: {
    maxWidth: 300,
  },
  footerBrandDesktop: {
    flex: 1,
    maxWidth: 400,
  },
  footerLogo: {
    fontSize: 20,
    fontWeight: '700',
    color: brandColors.white,
    marginBottom: 12,
  },
  footerTagline: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
    lineHeight: 22,
  },
  footerLinks: {
    gap: 12,
  },
  footerLinksTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: brandColors.white,
    marginBottom: 8,
  },
  footerLink: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.6)',
  },
  footerBottom: {
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    paddingTop: 24,
    gap: 8,
  },
  footerCopyright: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
  footerDisclaimer: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
  },
});
